
import * as Sentry from '@sentry/react'
import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'
import _ from 'lodash'
import moment from 'moment'
import toast from 'react-hot-toast'

import {
  getUserPool,
  getCognitoUser,
  getCurrentUser,
  setCurrentUser,
  getIdToken
} from '../../lib/cognito.js'
import { browserHistory } from '../../lib/history.js'
import http from '../../lib/http'
import { notify } from '../../lib/Notification/actions.js'
import { stopPubsubListening } from '../../lib/pubsub'
import * as toastStack from '../shared/toast-stack/actions.js'

export const INIT_STATUS = 'INIT_STATUS'
export const GET_STATUS = 'GET_STATUS'
export const UPDATE_STATUS = 'UPDATE_STATUS'
export const USER_AUTHENTICATED = 'auth/USER_AUTHENTICATED'
export const USER_ALREADY_AUTHENTICATED = 'auth/USER_ALREADY_AUTHENTICATED'
export const USER_SIGNED_OUT = 'auth/USER_SIGNED_OUT'
export const USER_PROFILE_UPDATED = 'auth/USER_PROFILE_UPDATED'
export const USER_PROFILE_APP_METADATA = 'auth/USER_PROFILE_APP_METADATA'
export const USER_AUTHENTICATION_FAILURE = 'auth/USER_AUTHENTICATION_FAILURE'
export const SET_LOGIN_FORM_SCREEN = 'auth/SET_LOGIN_FORM_SCREEN'
export const SET_ERROR = 'auth/SET_ERROR'
export const SET_FLASH_MESSAGE = 'auth/SET_FLASH_MESSAGE'
export const RESET_PASSWORD_FLOW_STARTED = 'auth/RESET_PASSWORD_FLOW_STARTED'
export const NEW_PASSWORD_FLOW_STARTED = 'auth/NEW_PASSWORD_FLOW_STARTED'
export const SIGNIN_FLOW_STARTED = 'auth/SIGNIN_FLOW_STARTED'
export const RESET_STATE = 'auth/RESET_STATE'
export const SET_LOADING = 'auth/SET_LOADING'

export const loginFormInit = () => ({ type: RESET_STATE })

export const userAlreadyAuthenticated = () => ({
  type: USER_ALREADY_AUTHENTICATED,
  redirectTo: '/'
})

export const setLoginFormScreen = (name, meta) => ({
  type: SET_LOGIN_FORM_SCREEN,
  screen: name,
  meta
})

export const setFlashMessage = message => ({
  type: SET_FLASH_MESSAGE,
  message
})

export const setError = error => ({
  type: SET_ERROR,
  error
})

export const userAuthenticationFailure = error => ({
  type: USER_AUTHENTICATION_FAILURE,
  error
})

export const setLoading = value => dispatch =>
  dispatch({
    type: SET_LOADING,
    loading: value
  })

let heartbeatInterval = null

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
}

const heartbeat = () => {
  getIdToken(false)
    .catch(() => {
      stopHeartbeat()
      browserHistory.replace('/login')
    })
}

const startHeartbeat = () => {
  if (!heartbeatInterval) {
    heartbeatInterval = setInterval(heartbeat, 1000)
  }
}

export const startResetPasswordFlow =
  (email, dropCurrentUser) => dispatch => {
    const formattedEmail = email.toLowerCase()

    if (dropCurrentUser && getCurrentUser()) {
      getCurrentUser()
        .signOut()
      setCurrentUser(null)
    }

    const user = new CognitoUser({
      Username: formattedEmail,
      Pool: getUserPool()
    })

    dispatch({
      type: RESET_PASSWORD_FLOW_STARTED,
      email: formattedEmail
    })

    user.forgotPassword({
      onSuccess: data => {
        const {
          CodeDeliveryDetails: {
            Destination: destination,
            DeliveryMedium: deliveryMedium
          }
        } = data
        dispatch(
          setLoginFormScreen('verification-code', {
            destination,
            deliveryMedium
          })
        )
      },
      onFailure: err => {
        dispatch(setError(err.message))
      }
    })
  }

const prepareProfile = tkn =>
  _.omitBy(
    Object.assign(tkn, {
      app_metadata: { roles: [] },
      user_metadata: {
        given: tkn.given_name,
        family: tkn.family_name,
        suffix: tkn['custom:name_suffix'] ? [ tkn['custom:name_suffix'] ] : []
      }
    }),
    (value, key) => key.match(/^custom:app_metadata_\d+/)
  )

export const userAuthenticated = profile => dispatch => {
  profile = prepareProfile(profile)

  Sentry.setUser({
    id: profile.sub,
    email: profile.email,
    name: [ profile.given_name, profile.family_name ]
      .map(v => v?.trim())
      .filter(Boolean)
      .join(' ')
  })

  dispatch({
    type: USER_AUTHENTICATED,
    // redirectTo: redirect || "/",
    profile
  })
}

const finishSignin = (session, dispatch, data) => {
  startHeartbeat()
  const something = data || getCurrentUser()._data

  if (something) {
    const { email, password } = something

    return http
      .getRequest('/auth/checkClients', {}, {
        auth: session.idToken.jwtToken
      })
      .then((resp) => {
        if (resp.status !== 200) {
          dispatch(userAuthenticationFailure('Your current profile does not have access to any data within Compassion. Please contact your Lucent Health Account Executive to have data access granted to your account.'))
          dispatch(
            setError(
              'Your current profile does not have access to any data within Compassion. Please contact your Lucent Health Account Executive to have data access granted to your account.'
            )
          )
          toast.error('Your current profile does not have access to any data within Compassion.\nPlease contact your Lucent Health Account Executive to have data access granted to your account.')
          throw new Error('No clients')
        }
      })
      .then(() => http
        .postRequest('/auth/validatePasswordAge', { password }, {
          auth: session.idToken
            .jwtToken
        }))
      .then(resp => {
        const { age, forceResetAge, warningAge } = resp.data

        if (age >= forceResetAge) {
          return getCognitoUser()
            .then(user => {
              user.signOut()
              dispatch(startResetPasswordFlow(email, true))
              dispatch(
                setError(
                  'Your password has expired, please set a new password below.'
                )
              )
            })
        }
        if (age > forceResetAge - warningAge) {
          const time = moment()
            .add(forceResetAge - age, 'seconds')
          dispatch(
            notify(`Your password will expire ${time.fromNow()}`),
            true
          )
          dispatch(userAuthenticated(session.idToken.payload))
        } else {
          dispatch(userAuthenticated(session.idToken.payload))
        }
      })
      .catch(() =>
        getCognitoUser()
          .then(user => {
            user.signOut()
          }))
  }
}

export const startNewPasswordFlow = () => dispatch => {
  dispatch({
    type: NEW_PASSWORD_FLOW_STARTED
  })
}

const makeDispatcher = (user, dispatch, data) => ({
  onSuccess: session => {
    finishSignin(session, dispatch, data)
  },
  onFailure: error => {
    if (error.code === 'UserNotConfirmedException') {
      dispatch(userAuthenticationFailure(
        'Your account was not confirmed yet'
      ))
    } else if (error.code === 'PasswordResetRequiredException') {
      dispatch(startResetPasswordFlow(user.username, false))
    } else if (
      error.code === 'CodeMismatchException' ||
      (error.code === 'InvalidParameterException' &&
        error.message === 'Missing required parameter SMS_MFA_CODE')
    ) {
      dispatch(setError('Invalid verification code'))
    } else if (error.code === 'UserNotFoundException') {
      dispatch(userAuthenticationFailure('Incorrect email or password'))
    } else if (error.code) {
      dispatch(userAuthenticationFailure('Incorrect email or password'))
    } else {
      dispatch(userAuthenticationFailure(error.message))
    }
  },
  mfaRequired: () => {
    const extended = user
    extended._data = data
    setCurrentUser(extended)
    dispatch(setLoginFormScreen('mfa-verification-code'))
  },
  newPasswordRequired: () => {
    const extended = user
    extended._data = data
    setCurrentUser(extended)
    dispatch(startNewPasswordFlow())
  }
})

export const userProfileUpdated = profile => dispatch => {
  profile = prepareProfile(profile)

  Sentry.setUser({
    id: profile.sub,
    email: profile.email,
    name: [ profile.given_name, profile.family_name ]
      .map(v => v?.trim())
      .filter(Boolean)
      .join(' ')
  })

  dispatch({
    type: USER_PROFILE_UPDATED,
    profile
  })
}

const STATUS = {
  timeout: 1,
  get: {
    current: 0,
    completed: 0
  },
  set: {
    current: 0,
    completed: 0
  }
}

export function getStatus () {
  return dispatch => {
    if (
      STATUS.get.current === STATUS.get.completed &&
      STATUS.set.current === STATUS.set.completed
    ) {
      const progress = ++STATUS.get.current
      dispatch({ type: INIT_STATUS })
      http.getRequest('/status')
        .then(data => {
          if (progress > STATUS.get.completed) {
            STATUS.get.completed = progress
          }
          if (progress === STATUS.get.current) {
            dispatch({
              type: GET_STATUS,
              data
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    }
  }
}

const putStatus = _.debounce(
  (params, resolve, reject) =>
    http
      .putRequest(
        '/status',
        _.pick(params, [ 'onduty', 'monitoring', 'oncall', 'concierge' ])
      )
      .then(resolve)
      .catch(reject),
  STATUS.timeout
)

export function setStatus (params) {
  return dispatch => {
    const progress = ++STATUS.set.current
    dispatch({
      type: UPDATE_STATUS,
      data: params
    })

    new Promise((resolve, reject) => putStatus(params, resolve, reject))
      .then(
        data => {
          if (progress > STATUS.set.completed) {
            STATUS.set.completed = progress
          }
          if (progress === STATUS.set.current) {
            dispatch({
              type: GET_STATUS,
              data
            })
            if (params.concierge) {
              // dispatch(getChats());
            }
          }
        }
      )
  }
}

export const signout = () =>
  getCognitoUser()
    .then(user => {
      setTimeout(user => {
        user?.signOut()
      }, 1000, user)

      return new Promise(resolve => {
        user.globalSignOut({
          onFailure: () => {
            setCurrentUser(null)
            resolve()
          },
          onSuccess: result => {
            user?.signOut()
            setCurrentUser(null)
            Sentry.setUser(null)
            resolve(result)
          }
        })
      })
    })
    .catch(() => Promise.resolve())

export const logout = (discardDialog, callback) => dispatch => {
  stopHeartbeat()
  stopPubsubListening()
  discardDialog?.reset()

  signout()
    .then(() => {
      setCurrentUser(null)
      dispatch({
        type: USER_SIGNED_OUT,
        forceRedirect: true
      })
      dispatch(toastStack.reset())
      callback?.()
    })
}

export const signin = (email, password) => dispatch => {
  const formattedEmail = email.toLowerCase()
    .trim()
    .replaceAll(' ', '')

  dispatch({
    type: SIGNIN_FLOW_STARTED,
    email: formattedEmail
  })

  const userPoolId = window.constants.COGNITO.poolId
  const clientId = window.constants.COGNITO.frontendClient.id
  const creds = new AuthenticationDetails({
    Username: formattedEmail,
    Password: password
  })

  const userPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId
  })

  const user = new CognitoUser({
    Username: formattedEmail,
    Pool: userPool
  })

  user.authenticateUser(
    creds,
    makeDispatcher(user, dispatch, {
      email,
      password
    })
  )
}

export const submitMFACode = (email, code) => dispatch => {
  dispatch(setLoading(true))

  const user = getCurrentUser()
  user.sendMFACode(code, makeDispatcher(user, dispatch, null))

  dispatch(setLoading(false))
}

export const setNewPassword = password => dispatch =>
  getCognitoUser()
    .then(user =>
      user.completeNewPasswordChallenge(
        password,
        {},
        makeDispatcher(user, dispatch)
      ))

export const resetPassword = (email, code, password) => dispatch => {
  dispatch(setLoading(true))
  const formattedEmail = email?.toLowerCase()
  let userPromise = null

  if (getCurrentUser()) {
    userPromise = getCognitoUser()
  } else {
    userPromise = Promise.resolve(
      new CognitoUser({
        Username: formattedEmail,
        Pool: getUserPool()
      })
    )
  }

  return http
    .postRequest('/auth/checkIfPasswordWasUsedBefore', {
      email,
      password
    }, { auth: false })
    .then(() => {
      userPromise.then(user =>
        user.confirmPassword(code, password, {
          onSuccess: () => {
            dispatch(setFlashMessage(
              'Password was successfully changed'
            ))
            dispatch(setLoginFormScreen('login'))
          },
          onFailure: err => {
            dispatch(
              setError(
                err.message.replace(
                  'greater than or equal to 6',
                  'greater than or equal to 8'
                )
              )
            )
          }
        }))
    })
    .catch(err => {
      // Password has been used before message
      dispatch(setError(err.response.data.message))
    })
}

export const signinFromCognitoSession = dispatch =>
  getCognitoUser()
    .then(user => {
      if (!user) {
        return
      }

      startHeartbeat()
      dispatch(
        userProfileUpdated(user.getSignInUserSession()
          .getIdToken()
          .payload)
      )
    })
